import {
  pluck, mergeRight, fromPairs, uniq, mergeDeepLeft, mergeDeepRight, omit, without,
} from 'ramda';
import { createSlice } from '@reduxjs/toolkit';
import { pickExisting } from '@twnel/web-components';
import {
  MESSAGES, LOGOUT_USER, UPDATE_CONVERSATIONS,
} from '../namespace';

const emptyList = () => ({
  info: {},
  byId: {},
  allIds: [],
});

const updateList = (list = emptyList(), { messages, insert, leftMerge }) => {
  let messageIds;
  const pairs = messages.map((message) => [message.id, message]);
  if (insert === 'replace') {
    messageIds = pluck(0, pairs);
  } else if (insert === 'left') {
    messageIds = [...pluck(0, pairs), ...list.allIds];
  } else {
    messageIds = [...list.allIds, ...pluck(0, pairs)];
  }
  return mergeRight(list, {
    byId: (leftMerge ? mergeDeepLeft : mergeDeepRight)(
      list.byId,
      fromPairs(pairs),
    ),
    allIds: uniq(messageIds),
  });
};

const updateInfo = (list = emptyList(), payload) => mergeDeepRight(list, {
  info: pickExisting(['complete', 'upToDate', 'nextPageMarker'], payload),
});

const deleteFromList = (list = emptyList(), { messageIds }) => ({
  ...list,
  byId: omit(messageIds, list.byId),
  allIds: without(messageIds, list.allIds),
});

const messagesSlice = createSlice({
  name: MESSAGES,
  initialState: {},
  reducers: {
    update: {
      reducer: (state, { payload }) => {
        state[payload.conversationId] = updateList(state[payload.conversationId], payload);
      },
      prepare: (conversationId, messages, insert) => ({
        payload: { conversationId, messages, insert },
      }),
    },
    update_info: {
      reducer: (state, { payload }) => {
        state[payload.conversationId] = updateInfo(state[payload.conversationId], payload);
      },
      prepare: (conversationId, { complete, upToDate, nextPageMarker }) => ({
        payload: {
          conversationId, complete, upToDate, nextPageMarker,
        },
      }),
    },
    delete: {
      reducer: (state, { payload }) => {
        state[payload.conversationId] = deleteFromList(state[payload.conversationId], payload);
      },
      prepare: (conversationId, messageIds) => ({
        payload: { conversationId, messageIds },
      }),
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(UPDATE_CONVERSATIONS, (state, { payload: { conversation } }) => {
        if (conversation.lastMessage) {
          state[conversation.id] = updateList(state[conversation.id], {
            messages: [conversation.lastMessage],
            insert: 'right',
            leftMerge: true,
          });
        }
      })
      .addCase(LOGOUT_USER, () => ({}));
  },
});

export const {
  update: updateMessages,
  update_info: updateMessagesInfo,
  delete: deleteMessages,
} = messagesSlice.actions;

export default { [MESSAGES]: messagesSlice.reducer };
